import { useState, useEffect, forwardRef } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Typography,
  InputLabel,
  Slide,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
} from "@mui/material";
import { LuX, LuChevronDown, LuCheck } from "react-icons/lu";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { createHelpRequest } from "../../redux/reducers/helpCenter/helpCenterService";
import { clearAuth } from "../../redux/reducers/auth/authSlice";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const HelpCenterDialog = ({ open, onClose }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const isLoggedIn = !!user;
  const { loading } = useSelector((state) => state.helpCenter);

  const [reasons, setReasons] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  // Create validation schema with translations
  const validationSchema = yup.object({
    email: yup
      .string()
      .trim()
      .email(t("helpCenter.validation.validEmail"))
      .min(5, t("helpCenter.validation.minEmail", { min: 5 }))
      .max(254, t("helpCenter.validation.maxEmail", { max: 254 }))
      .required(t("helpCenter.validation.requiredEmail")),
    reason: yup.string().required(t("helpCenter.validation.requiredReason")),
    message: yup
      .string()
      .required(t("helpCenter.validation.requiredMessage"))
      .min(10, t("helpCenter.validation.minMessage", { min: 10 }))
      .max(1000, t("helpCenter.validation.maxMessage", { max: 1000 })),
  });

  const fetchReasons = async () => {
    setIsFetching(true);
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      };
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}help-center/list`,
        config
      );

      if (response.data?.data) {
        setReasons(response.data.data);
      }
    } catch (error) {
      if (
        error.response &&
        (error.response.status === 400 || error.response.status === 401)
      ) {
        dispatch(clearAuth());
      } else {
        enqueueSnackbar(t("helpCenter.errors.fetchReasons"), {
          variant: "error",
        });
      }

      console.error("Error fetching categories:", error);
    }
    setIsFetching(false);
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchReasons();
    }
  }, [isLoggedIn]);

  const formik = useFormik({
    initialValues: {
      email: "",
      reason: "",
      message: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          email: values.email,
          reason: values.reason,
          message: values.message,
        };

        const resultAction = await dispatch(createHelpRequest(payload));

        if (createHelpRequest.fulfilled.match(resultAction)) {
          onClose();
          formik.resetForm();
        }
      } catch (error) {
        console.error("Help request error:", error);
      }
    },
  });

  return (
    <Dialog
      TransitionComponent={Transition}
      keepMounted
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 600 }}>
            {t("helpCenter.title")}
          </Typography>
          <IconButton
            onClick={() => {
              formik.resetForm();
              onClose();
            }}
          >
            <LuX size={24} color="black" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <form onSubmit={formik.handleSubmit}>
          <InputLabel htmlFor="email">
            {t("helpCenter.fields.email")}
            <Typography color="error" display="inline" fontWeight={500}>
              *
            </Typography>
          </InputLabel>
          <TextField
            fullWidth
            id="email"
            name="email"
            placeholder={t("helpCenter.placeholders.email")}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            inputProps={{ maxLength: 254 }}
            sx={{ mt: 0.5 }}
          />

          <Box sx={{ mt: 2 }}>
            <InputLabel htmlFor="reason">
              {t("helpCenter.fields.reason")}
              <Typography color="error" display="inline" fontWeight={500}>
                *
              </Typography>
            </InputLabel>
            <Select
              sx={{
                mt: 0.5,
                "& .MuiSelect-select": {
                  color: (theme) =>
                    formik.values.reason
                      ? theme.palette.text.primary
                      : "#9e9e9e",
                },
              }}
              variant="outlined"
              fullWidth
              id="reason"
              name="reason"
              displayEmpty
              value={formik.values.reason}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.reason && Boolean(formik.errors.reason)}
              IconComponent={(props) => <LuChevronDown size={20} {...props} />}
              disabled={isFetching}
              renderValue={(selected) => {
                if (!selected)
                  return isFetching
                    ? t("helpCenter.loading")
                    : t("helpCenter.placeholders.reason");
                return selected;
              }}
            >
              <MenuItem value="" disabled>
                {isFetching
                  ? t("helpCenter.loading")
                  : t("helpCenter.placeholders.reason")}
              </MenuItem>

              {!isFetching && reasons.length > 0 ? (
                reasons.map((reason, index) => (
                  <MenuItem
                    key={reason.title}
                    value={reason.title}
                    sx={{
                      borderRadius: "4px !important",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      borderBottom:
                        index !== reasons.length - 1
                          ? "1px solid #e0e0e0"
                          : "none",
                    }}
                  >
                    {reason.title}
                    {formik.values.reason === reason.title && <LuCheck />}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  {t("helpCenter.noReasonsAvailable")}
                </MenuItem>
              )}
            </Select>

            {formik.touched.reason && formik.errors.reason && (
              <Typography color="error" variant="caption" sx={{ ml: 1.5 }}>
                {formik.errors.reason}
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <InputLabel htmlFor="message">
              {t("helpCenter.fields.message")}
              <Typography
                component="span"
                color="error"
                display="inline"
                fontWeight={500}
              >
                *
              </Typography>
            </InputLabel>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              id="message"
              name="message"
              placeholder={t("helpCenter.placeholders.message")}
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.message && Boolean(formik.errors.message)}
              helperText={formik.touched.message && formik.errors.message}
              sx={{
                mt: 0.5,
                "& .MuiOutlinedInput-root": {
                  height: "auto !important",
                  padding: "12px 16px",
                  backgroundColor: "#f8f8f8",
                  alignItems: "flex-start",
                },
                "& .MuiInputBase-input": {
                  padding: "0 !important",
                  lineHeight: 1.5,
                },
              }}
            />
          </Box>

          <LoadingButton
            fullWidth
            type="submit"
            variant="contained"
            loading={loading}
            loadingIndicator={<CircularProgress size={24} />}
            sx={{ mt: 3 }}
          >
            {t("helpCenter.submit")}
          </LoadingButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default HelpCenterDialog;
